import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import {
  AutocompleteSingleSelect,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Textarea,
  toast,
} from '@shigo-ui/ui';

import { showRequestError } from '../../../../../lib/requestErrorToast';
import { dashboardT } from '../../../../../lib/messages';
import { useBusyDialogGuard } from '../../../../../hooks/useBusyDialogGuard';
import {
  createAppFromRequestInitialValue,
  createAppFromRequestSchema,
  serializeCreateAppFromRequest,
  type CreateAppFromRequestFormValue,
} from '../../appRequestFormSchema';
import {
  useCollectedAppDataQuery,
  useSupplyAppsMutations,
  useSupplyAppsReferences,
} from '../../hooks';
import type { AppRequest } from '../../model';

export function CreateAppFromRequestDialog({
  request,
  onClose,
}: {
  request: AppRequest;
  onClose: () => void;
}) {
  'use no memo';
  const form = useForm<CreateAppFromRequestFormValue>({
    resolver: zodResolver(createAppFromRequestSchema),
    defaultValues: createAppFromRequestInitialValue(request),
  });
  const references = useSupplyAppsReferences();
  const collected = useCollectedAppDataQuery(request.publisherId, request.appStoreUrl);
  const mutation = useSupplyAppsMutations().createFromRequest;
  const errors = form.formState.errors;
  const dialogGuard = useBusyDialogGuard(mutation.isPending, onClose);
  const hydratedRef = React.useRef(false);
  const lookupErrorShownRef = React.useRef(false);
  React.useEffect(() => {
    if (!collected.data || hydratedRef.current) return;
    hydratedRef.current = true;
    const current = form.getValues();
    form.reset({
      ...current,
      name: collected.data.name ?? current.name,
      appStoreUrl: collected.data.appStoreUrl ?? current.appStoreUrl,
      appStoreId: collected.data.appStoreId ?? current.appStoreId,
      platformId: collected.data.platform?.id ?? current.platformId,
      bundleId: request.bundleId ?? collected.data.bundleId ?? current.bundleId,
      domain: collected.data.domain ?? current.domain,
      adsTxtLocation: collected.data.adsTxtLocation ?? current.adsTxtLocation,
      iabCategoryIds:
        collected.data.iabCategoriesIds.length > 0
          ? collected.data.iabCategoriesIds.slice(0, 1)
          : collected.data.iabCategoryIds.slice(0, 1),
      description: collected.data.description ?? current.description,
    });
  }, [collected.data, form, request.bundleId]);
  React.useEffect(() => {
    if (!collected.isError || lookupErrorShownRef.current) return;
    lookupErrorShownRef.current = true;
    toast.info(dashboardT('supplyApps.request.lookupError'));
  }, [collected.isError]);
  async function submit(value: CreateAppFromRequestFormValue) {
    if (!request.publisherId) {
      toast.error(dashboardT('supplyApps.request.createError'));
      return;
    }
    try {
      await mutation.mutateAsync({
        publisherId: request.publisherId,
        payload: serializeCreateAppFromRequest(value),
        request: { ...request, blocked: value.blocked, note: value.note },
      });
      toast.success(dashboardT('supplyApps.request.createdApp'));
      onClose();
    } catch (error) {
      showRequestError(error, dashboardT('supplyApps.request.createError'));
    }
  }
  return (
    <Dialog open onOpenChange={dialogGuard.onOpenChange}>
      <DialogContent
        className="max-w-3xl gap-0 p-0"
        onEscapeKeyDown={dialogGuard.onDismissInteraction}
        onPointerDownOutside={dialogGuard.onDismissInteraction}
      >
        <DialogHeader className="border-border border-b px-5 py-4">
          <DialogTitle className="text-[15px]">
            {dashboardT('supplyApps.request.createTitle')}
          </DialogTitle>
          <DialogDescription>
            {dashboardT('supplyApps.request.createDescription')}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form noValidate onSubmit={(event) => void form.handleSubmit(submit)(event)}>
            <div className="max-h-[72vh] overflow-y-auto px-5 py-5">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1.5 md:col-span-2">
                  <Label htmlFor="request-app-name" className="text-[13px] font-semibold">
                    {dashboardT('supplyApps.name')} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="request-app-name"
                    required
                    aria-invalid={Boolean(errors.name)}
                    aria-describedby={errors.name ? 'request-app-name-error' : undefined}
                    {...form.register('name')}
                  />
                  {errors.name ? (
                    <p
                      id="request-app-name-error"
                      role="alert"
                      className="text-destructive text-xs"
                    >
                      {errors.name.message}
                    </p>
                  ) : null}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="request-store-url" className="text-[13px] font-semibold">
                    {dashboardT('supplyApps.appStoreUrl')}{' '}
                    <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="request-store-url"
                    required
                    aria-invalid={Boolean(errors.appStoreUrl)}
                    aria-describedby={errors.appStoreUrl ? 'request-store-url-error' : undefined}
                    {...form.register('appStoreUrl')}
                  />
                  {errors.appStoreUrl ? (
                    <p
                      id="request-store-url-error"
                      role="alert"
                      className="text-destructive text-xs"
                    >
                      {errors.appStoreUrl.message}
                    </p>
                  ) : null}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="request-store-id" className="text-[13px] font-semibold">
                    {dashboardT('supplyApps.appStoreId')}{' '}
                    <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="request-store-id"
                    required
                    aria-invalid={Boolean(errors.appStoreId)}
                    aria-describedby={errors.appStoreId ? 'request-store-id-error' : undefined}
                    {...form.register('appStoreId')}
                  />
                  {errors.appStoreId ? (
                    <p
                      id="request-store-id-error"
                      role="alert"
                      className="text-destructive text-xs"
                    >
                      {errors.appStoreId.message}
                    </p>
                  ) : null}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="request-platform" className="text-[13px] font-semibold">
                    {dashboardT('supplyApps.platform')} <span className="text-destructive">*</span>
                  </Label>
                  <Controller
                    control={form.control}
                    name="platformId"
                    render={({ field }) => (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger
                          id="request-platform"
                          className="h-9"
                          aria-label={dashboardT('supplyApps.platform')}
                          aria-invalid={Boolean(errors.platformId)}
                          aria-describedby={
                            errors.platformId ? 'request-platform-error' : undefined
                          }
                        >
                          <SelectValue placeholder={dashboardT('supplyApps.selectPlatform')} />
                        </SelectTrigger>
                        <SelectContent>
                          {(references.platforms.data ?? []).map((item) => (
                            <SelectItem key={item.id} value={item.id}>
                              {item.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {errors.platformId ? (
                    <p
                      id="request-platform-error"
                      role="alert"
                      className="text-destructive text-xs"
                    >
                      {errors.platformId.message}
                    </p>
                  ) : null}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="request-bundle-id" className="text-[13px] font-semibold">
                    {dashboardT('supplyApps.bundleId')} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="request-bundle-id"
                    required
                    aria-invalid={Boolean(errors.bundleId)}
                    aria-describedby={errors.bundleId ? 'request-bundle-id-error' : undefined}
                    {...form.register('bundleId')}
                  />
                  {errors.bundleId ? (
                    <p
                      id="request-bundle-id-error"
                      role="alert"
                      className="text-destructive text-xs"
                    >
                      {errors.bundleId.message}
                    </p>
                  ) : null}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="request-domain" className="text-[13px] font-semibold">
                    {dashboardT('supplyApps.domain')}
                  </Label>
                  <Input id="request-domain" {...form.register('domain')} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="request-publisher" className="text-[13px] font-semibold">
                    {dashboardT('supplyApps.publisher')}
                  </Label>
                  <Input id="request-publisher" value={request.publisherName ?? ''} readOnly />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="request-adstxt" className="text-[13px] font-semibold">
                    {dashboardT('supplyApps.adsTxtLocation')}
                  </Label>
                  <Input id="request-adstxt" {...form.register('adsTxtLocation')} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[13px] font-semibold">
                    {dashboardT('supplyApps.iabCategories')}
                  </Label>
                  <Controller
                    control={form.control}
                    name="iabCategoryIds"
                    render={({ field }) => (
                      <AutocompleteSingleSelect
                        label={dashboardT('supplyApps.iabCategories')}
                        options={(references.iabCategories.data ?? []).map((item) => ({
                          id: item.id,
                          label: item.name,
                        }))}
                        value={field.value[0] ?? ''}
                        onValueChange={(value) => {
                          field.onChange(value ? [value] : []);
                        }}
                        loading={references.iabCategories.isLoading}
                        placeholder={dashboardT('supplyApps.selectIabCategories')}
                      />
                    )}
                  />
                </div>
                <div className="space-y-1.5 md:col-span-2">
                  <Label htmlFor="request-description" className="text-[13px] font-semibold">
                    {dashboardT('supplyApps.description')}
                  </Label>
                  <Textarea id="request-description" rows={3} {...form.register('description')} />
                </div>
                <Controller
                  control={form.control}
                  name="blocked"
                  render={({ field }) => (
                    <div className="border-input flex items-center justify-between rounded-md border px-3 py-2">
                      <Label htmlFor="create-request-blocked">
                        {dashboardT('supplyApps.request.blocked')}
                      </Label>
                      <Switch
                        id="create-request-blocked"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </div>
                  )}
                />
                <div className="space-y-1.5">
                  <Label htmlFor="create-request-note" className="text-[13px] font-semibold">
                    {dashboardT('supplyApps.request.note')}
                  </Label>
                  <Input id="create-request-note" {...form.register('note')} />
                </div>
              </div>
            </div>
            <DialogFooter className="border-border border-t px-5 py-4">
              <Button
                type="button"
                variant="outline"
                disabled={mutation.isPending}
                onClick={dialogGuard.close}
              >
                {dashboardT('supplyApps.cancel')}
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {dashboardT(
                  mutation.isPending ? 'supplyApps.request.saving' : 'supplyApps.request.createApp',
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
